/**
 * A public post, open to anyone on the web (no account): its author's profile card, as on the activity feed, and
 * the post under it — nothing more (no comments, reactions or actions; those are for Otable users).
 * Reached at /p/post/<id>/. Backend: GET /interact/public/posts/<id>/ (only posts their author made public).
 */
import React from 'react';
import useSWR from 'swr';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Container, Link, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { fetcher } from 'shared/helpers/axios';
import LabMarkdown, { SafeImage } from 'components/collab/lab/LabMarkdown';
import FeedIdentityCard from 'components/social/profile/FeedIdentityCard';
import LinkPreviewCard from 'components/common/LinkPreviewCard';
import YouTubeEmbed from 'components/common/YouTubeEmbed';
import RepostedOriginal from 'components/common/RepostedOriginal';
import { linkedImageOf, postMarkdown } from 'shared/helpers/postText';
import { findYouTube, parseYouTube } from 'shared/helpers/youtube';
import { plainMentions } from './mentions';
import otableLogo from 'media/otable_main.png';
import { useDocumentTitle } from 'shared/hooks/useDocumentTitle';

// the post's text for visitors: mentions as plain names (profiles are for Otable users)
const publicText = (post) => postMarkdown(plainMentions(post.title ? `**${post.title}**\n\n${post.body || ''}` : post.body || ''));

function Attachment({ post }) {
  const preview = post.link_preview;
  const video = findYouTube(preview?.url, post.body);
  if (video) return <YouTubeEmbed video={video} title={parseYouTube(preview?.url) ? preview?.title : undefined} />;
  if (!preview?.url) return null;
  return linkedImageOf(preview) ? <SafeImage src={linkedImageOf(preview)} alt="" /> : <LinkPreviewCard preview={preview} />;
}

export default function PublicPostPage() {
  const { postId } = useParams();
  const { data: post, error } = useSWR(postId ? `/interact/public/posts/${postId}/` : null, fetcher, { shouldRetryOnError: false });
  useDocumentTitle(post?.author?.name ? `${post.author.name} on Otable` : null);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', py: { xs: 2, sm: 4 } }}>
      <Container maxWidth="sm">
        {error && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" gutterBottom>This post is not public</Typography>
            <Typography color="text.secondary">Its author shared it with Otable users only, or it was removed.</Typography>
          </Box>
        )}
        {!post && !error && <Box display="grid" sx={{ placeItems: 'center', py: 10 }}><CircularProgress /></Box>}
        {post && (
          <>
            <FeedIdentityCard user={{ avatar: post.author.avatar }} name={post.author.name} hideCounts
              headline={[post.author.headline, post.author.organization].filter(Boolean).join(' · ')} />
            <Box sx={{ bgcolor: 'background.paper', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', p: { xs: 2, sm: 3 } }}>
              <Typography variant="caption" color="text.secondary">
                {post.created ? formatDistanceToNow(new Date(post.created), { addSuffix: true }) : ''}
              </Typography>
              {(post.title || post.body) && (
                <Box sx={{ mt: 1, wordBreak: 'break-word', '& p': { mb: 0.75 } }}>
                  <LabMarkdown images hashtags>{publicText(post)}</LabMarkdown>
                </Box>
              )}
              <Attachment post={post} />
              {post.is_repost && (
                <RepostedOriginal linkable={false} original={post.original?.unavailable ? { unavailable: true, reason: 'hidden' }
                  : { ...post.original, author_name: post.original?.author?.name, body: plainMentions(post.original?.body || '') }} />
              )}
            </Box>
          </>
        )}
        <Box component="footer" sx={{ textAlign: 'center', mt: 4 }}>
          <Link href="https://www.otable.net" target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-block', opacity: 0.6 }}>
            <Box component="img" src={otableLogo} alt="Otable" sx={{ height: 30 }} />
          </Link>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Research collaboration, redesigned.</Typography>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button variant="contained" href="/register/" sx={{ textTransform: 'none' }}>Join Otable</Button>
            <Button href="/login/" sx={{ textTransform: 'none' }}>Sign in</Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
